import { useState } from 'react';
import { Modal, TextInput, Select, Button, Stack, Group, Alert, Text } from '@mantine/core';
import { IconUserPlus, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from '../context/TranslationContext';
import api from '../services/api';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CreateUserModal({ onClose, onSuccess, languages = [] }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) nextErrors.name = t('createUserNameRequired', 'Name is required');
    if (!trimmedEmail) {
      nextErrors.email = t('createUserEmailRequired', 'Email is required');
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      nextErrors.email = t('createUserEmailInvalid', 'Please enter a valid email address');
    }

    setErrors(nextErrors);
    setSubmitError(null);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSaving(true);
      const created = await api.createUser({
        name: trimmedName,
        email: trimmedEmail,
        preferred_language: preferredLanguage || null,
      });
      onSuccess(created);
    } catch (err) {
      setSubmitError(
        err.message?.toLowerCase().includes('already exists')
          ? t('createUserDuplicateEmail', 'A user with this email already exists')
          : (err.message || t('createUserError', 'Failed to create user'))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={t('createUserTitle', 'Create New User')}
      centered
      size="sm"
      closeButtonProps={{ 'aria-label': t('createUserClose', 'Close dialog') }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('createUserDescription', 'Create a customer account. The user can sign in later with Google or Facebook using the same email.')}
          </Text>

          {submitError && (
            <Alert
              role="alert"
              icon={<IconAlertCircle size={16} aria-hidden="true" />}
              title={t('createUserErrorTitle', 'Could not create user')}
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          )}

          <TextInput
            label={t('createUserName', 'Name')}
            placeholder={t('createUserNamePlaceholder', 'e.g. Anna Muster')}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            error={errors.name}
            autoComplete="name"
            data-autofocus
          />

          <TextInput
            label={t('createUserEmail', 'Email')}
            placeholder={t('createUserEmailPlaceholder', 'anna@example.com')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
            error={errors.email}
            autoComplete="email"
          />

          {languages.length > 1 && (
            <Select
              label={t('preferredLanguage', 'Preferred Language')}
              data={languages.map(l => ({ value: l.code, label: `${l.name} (${l.native_name})` }))}
              value={preferredLanguage}
              onChange={setPreferredLanguage}
              searchable
              clearable
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={saving}>
              {t('createUserCancel', 'Cancel')}
            </Button>
            <Button type="submit" leftSection={<IconUserPlus size={16} aria-hidden="true" />} loading={saving}>
              {t('createUserSubmit', 'Create User')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
